'use client';

import {
  BrokerProductSupportMap,
  DatabaseBroker,
  FrontendBroker,
  isNotNil,
} from '@trucksmarter/shared';
import { createContext, useContext, useMemo } from 'react';
import { useBrokerProductSupport } from '../BrokerProductSupportProvider/BrokerProductSupportProvider';

const BrokersContext = createContext<Partial<
  Record<DatabaseBroker, FrontendBroker>
> | null>(null);

export type BrokersFilter = (
  broker: FrontendBroker,
  brokerProductSupportMap: Partial<BrokerProductSupportMap>,
) => boolean;

export function useBrokers(
  filter?: BrokersFilter,
): Partial<Record<DatabaseBroker, FrontendBroker>> {
  const brokers = useContext(BrokersContext);
  const productSupportMap = useBrokerProductSupport();

  if (brokers === null) {
    throw new Error('useBrokers has to be used within <BrokersProvider>');
  }

  return useMemo(
    () =>
      filter
        ? Object.values(brokers)
            .filter(isNotNil)
            .filter((broker: FrontendBroker) =>
              filter(broker, productSupportMap),
            )
            .reduce(
              (dict, broker) => (
                (dict[broker.broker as DatabaseBroker] = broker), dict
              ),
              {} as Partial<Record<DatabaseBroker, FrontendBroker>>,
            )
        : brokers,
    [brokers, productSupportMap, filter],
  );
}

interface Props {
  value: Partial<Record<DatabaseBroker, FrontendBroker>>;
  children: React.ReactNode;
}

export default function BrokersProvider({ value, children }: Props) {
  return (
    <BrokersContext.Provider value={value}>{children}</BrokersContext.Provider>
  );
}
